import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2 } from "lucide-react";
import { useRegion } from "../contexts/RegionContext";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import DestinationGrid from "../components/DestinationGrid";

const STATS = [
    { k: "2 wk", v: "From kickoff to first demo" },
    { k: "60%", v: "Cost vs urban IT averages" },
    { k: "4", v: "Active regions · IN, AU, AE, Global" },
    { k: "100%", v: "Production-grade or refund" },
];

const Home = () => {
    const { region } = useRegion();
    useEffect(() => {
        document.title = "IndoBlitz · Built rural. Delivered globally.";
    }, []);

    return (
        <>
            <Nav />
            <main data-testid="home-page">
                {/* Compact, mobile-first hero */}
                <section id="top" className="relative pt-28 lg:pt-36 pb-10 lg:pb-14 overflow-hidden" data-testid="hero">
                    <div className="absolute inset-0 hero-grid mask-fade-b pointer-events-none" />
                    <div className="absolute inset-0 grain pointer-events-none" />

                    <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="pill"
                            data-testid="hero-pill"
                        >
                            <Globe2 className="w-3.5 h-3.5" />
                            <span>Rural-built · Globally delivered</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl text-ivory tracking-tight leading-[0.98] max-w-[16ch]"
                            data-testid="hero-headline"
                        >
                            Built rural.{" "}
                            <span className="font-display-wonk italic text-gold-shimmer">Delivered</span> globally.
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-6 max-w-xl text-base lg:text-lg text-ivory/65 leading-relaxed"
                            data-testid="hero-sub"
                        >
                            Cross-border software, talent and AI readiness - engineered in India, delivered
                            worldwide. Urban-IT quality at sensible, transparent pricing.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8 flex flex-wrap items-center gap-3"
                        >
                            <a href="#destinations" className="btn-gold" data-testid="hero-cta-primary">
                                Find your lane <ArrowRight className="w-4 h-4" />
                            </a>
                            <Link to="/services" className="btn-ghost" data-testid="hero-cta-secondary">
                                See pricing in {region.currency}
                            </Link>
                        </motion.div>
                    </div>
                </section>

                <DestinationGrid />

                {/* Trust strip */}
                <section className="relative pb-20 lg:pb-28" data-testid="hero-stats">
                    <div className="max-w-7xl mx-auto px-5 lg:px-10">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gold/10 border border-gold/10 rounded-2xl overflow-hidden">
                            {STATS.map((s, i) => (
                                <div key={i} className="bg-obsidian px-5 py-6">
                                    <div className="font-display text-3xl text-gold">{s.k}</div>
                                    <div className="mt-2 text-xs uppercase tracking-widest text-ivory/50">{s.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default Home;
